import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SelectedRoutine } from '../types';
import { getStorageItem, removeStorageItem, setStorageItem } from '../utils/storage';
import { OFFLINE_CACHE_KEYS } from '../utils/offlineCache';

export interface QueuedWorkout {
  id: string;
  clientId: string;
  userId: string;
  routine: SelectedRoutine;
  confirmedSets: string[];
  queuedAt: number;
  retries: number;
  status: 'pending' | 'failed';
}

const LOCK_TTL_MS = 30_000;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function createQueueId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback must still be a valid UUID: clientId is stored in a uuid column.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    return (char === 'x' ? random : (random % 4) + 8).toString(16);
  });
}

async function loadQueue(userId: string | null): Promise<QueuedWorkout[]> {
  if (!userId) return [];
  const raw = await getStorageItem(OFFLINE_CACHE_KEYS.queue(userId));
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw) as QueuedWorkout[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry) => entry && entry.userId === userId);
  } catch {
    return [];
  }
}

async function persistQueue(userId: string | null, queue: QueuedWorkout[]): Promise<void> {
  if (!userId) return;
  await setStorageItem(OFFLINE_CACHE_KEYS.queue(userId), JSON.stringify(queue));
}

async function acquireLock(userId: string): Promise<boolean> {
  const key = OFFLINE_CACHE_KEYS.queueLock(userId);
  const now = Date.now();
  const raw = await getStorageItem(key);

  if (raw) {
    try {
      const parsed = JSON.parse(raw) as { expiresAt?: number };
      if (parsed.expiresAt && parsed.expiresAt > now) return false;
    } catch {
      // Ignore malformed lock and overwrite it below.
    }
  }

  await setStorageItem(key, JSON.stringify({ expiresAt: now + LOCK_TTL_MS }));
  return true;
}

async function releaseLock(userId: string): Promise<void> {
  await removeStorageItem(OFFLINE_CACHE_KEYS.queueLock(userId));
}

export function useWorkoutQueue(userId: string | null) {
  const [queue, setQueue] = useState<QueuedWorkout[]>([]);

  useEffect(() => {
    let cancelled = false;

    loadQueue(userId).then((loaded) => {
      if (!cancelled) setQueue(loaded);
    });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const replaceQueue = useCallback(async (updater: (current: QueuedWorkout[]) => QueuedWorkout[]) => {
    if (!userId) return [] as QueuedWorkout[];

    const nextQueue = updater(queue);
    setQueue(nextQueue);
    await persistQueue(userId, nextQueue);
    return nextQueue;
  }, [queue, userId]);

  const enqueue = useCallback(async (routine: SelectedRoutine, confirmedSets: Set<string>): Promise<QueuedWorkout> => {
    if (!userId) {
      throw new Error('Usuario no disponible para guardar offline');
    }

    const queuedWorkout: QueuedWorkout = {
      id: createQueueId(),
      clientId: createQueueId(),
      userId,
      routine,
      confirmedSets: Array.from(confirmedSets),
      queuedAt: Date.now(),
      retries: 0,
      status: 'pending',
    };

    await replaceQueue((current) => [...current, queuedWorkout]);
    return queuedWorkout;
  }, [replaceQueue, userId]);

  const dequeue = useCallback(async (queueId: string): Promise<void> => {
    await replaceQueue((current) => current.filter((entry) => entry.id !== queueId));
  }, [replaceQueue]);

  const markFailed = useCallback(async (queueId: string): Promise<void> => {
    await replaceQueue((current) =>
      current.map((entry) =>
        entry.id !== queueId
          ? entry
          : {
              ...entry,
              retries: entry.retries + 1,
              status: entry.retries + 1 >= 3 ? 'failed' : 'pending',
            }
      )
    );
  }, [replaceQueue]);

  const clearQueue = useCallback(async (): Promise<void> => {
    if (!userId) {
      setQueue([]);
      return;
    }
    setQueue([]);
    await persistQueue(userId, []);
    await releaseLock(userId);
  }, [userId]);

  const syncQueue = useCallback(async (
    persistFn: (entry: QueuedWorkout) => Promise<void>
  ): Promise<void> => {
    if (!userId) return;

    const locked = await acquireLock(userId);
    if (!locked) return;

    try {
      // Entries queued by older builds may carry a non-UUID clientId, which the
      // uuid column rejects. Such an id never reached the server, so replacing
      // it is safe; persist it so retries reuse the same one.
      const loaded = await loadQueue(userId);
      const snapshot = loaded.map((entry) =>
        UUID_PATTERN.test(entry.clientId) ? entry : { ...entry, clientId: createQueueId() }
      );
      if (snapshot.some((entry, index) => entry !== loaded[index])) {
        setQueue(snapshot);
        await persistQueue(userId, snapshot);
      }

      for (const entry of snapshot) {
        if (entry.status !== 'pending' && entry.status !== 'failed') continue;

        try {
          await persistFn(entry);
          const afterSuccess = await loadQueue(userId);
          const nextQueue = afterSuccess.filter((current) => current.id !== entry.id);
          setQueue(nextQueue);
          await persistQueue(userId, nextQueue);
        } catch {
          const afterError = await loadQueue(userId);
          const nextQueue: QueuedWorkout[] = afterError.map((current) =>
            current.id !== entry.id
              ? current
              : {
                  ...current,
                  retries: current.retries + 1,
                  status: current.retries + 1 >= 3 ? 'failed' : 'pending',
                }
          );
          setQueue(nextQueue);
          await persistQueue(userId, nextQueue);
        }
      }
    } finally {
      await releaseLock(userId);
    }
  }, [userId]);

  const pendingCount = useMemo(
    () => queue.filter((entry) => entry.status === 'pending').length,
    [queue]
  );

  return {
    queue,
    pendingCount,
    enqueue,
    dequeue,
    markFailed,
    clearQueue,
    syncQueue,
  };
}
